import React, { useEffect, useState } from 'react';
import {
    Alert,
    Card,
    Descriptions,
    Drawer,
    Empty,
    Skeleton,
    Space,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    aiAdminApi,
    SandboxRunDetail,
    SandboxRunOutput,
    SandboxRunStatus,
    SubsystemId,
} from '../../../services/ai-admin-api';
import { SideBySideLineDiff } from '../shared/SideBySideLineDiff';

const { Text, Paragraph } = Typography;

const STATUS_COLOUR: Record<SandboxRunStatus, string> = {
    queued: 'blue',
    running: 'processing',
    complete: 'green',
    error: 'red',
};

function getOutputText(side: SandboxRunOutput | null): string {
    if (!side) return '';
    const json = side.outputJson as Record<string, unknown> | null;
    if (!json) return '';
    if (typeof json.text === 'string') return json.text;
    // Orbital AI returns a 3-item string array. Render as a numbered list so
    // the side-by-side comparison stays readable.
    if (Array.isArray(json.items)) {
        return (json.items as unknown[])
            .map((item, idx) => `${idx + 1}. ${String(item)}`)
            .join('\n\n');
    }
    return JSON.stringify(json, null, 2);
}

interface SideBySideTextProps {
    a: string;
    b: string;
    aPending?: boolean;
    bPending?: boolean;
}

/**
 * Three-dot pulsing indicator shown in an output panel while that side is
 * still being generated. Pure CSS via inline @keyframes — no external lib
 * needed.
 */
const TypingDots: React.FC = () => (
    <span style={{ display: 'inline-block' }}>
        <style>
            {`@keyframes sandbox-typing-dot {
                0%, 80%, 100% { opacity: 0.2; }
                40% { opacity: 1; }
            }`}
        </style>
        {[0, 1, 2].map((i) => (
            <span
                key={i}
                style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    margin: '0 3px',
                    borderRadius: '50%',
                    background: '#999',
                    animation: 'sandbox-typing-dot 1.4s infinite ease-in-out',
                    animationDelay: `${i * 0.2}s`,
                }}
            />
        ))}
    </span>
);

/**
 * Side-by-side full-text panel for AI outputs. Unlike a line diff this does
 * NOT highlight differences — LLM outputs vary run-to-run even with identical
 * config, so a diff would just paint everything red/green and add noise.
 * Admins compare the two outputs qualitatively.
 *
 * While the run is in progress, sides without output yet render an animated
 * typing-dots indicator instead of "(empty)" so the drawer doesn't look
 * stuck.
 */
const SideBySideText: React.FC<SideBySideTextProps> = ({
    a,
    b,
    aPending,
    bPending,
}) => (
    <div
        style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            overflow: 'hidden',
        }}
    >
        <div
            style={{
                padding: '8px 12px',
                background: '#fff1f0',
                borderRight: '1px solid #f0f0f0',
                fontWeight: 500,
                fontSize: 13,
            }}
        >
            Side A
        </div>
        <div
            style={{
                padding: '8px 12px',
                background: '#f6ffed',
                fontWeight: 500,
                fontSize: 13,
            }}
        >
            Side B
        </div>
        <div
            style={{
                padding: '12px 16px',
                background: '#fff',
                borderRight: '1px solid #f0f0f0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 13,
                lineHeight: 1.6,
                minHeight: 120,
            }}
        >
            {a ? (
                a
            ) : aPending ? (
                <TypingDots />
            ) : (
                <Text type="secondary">(empty)</Text>
            )}
        </div>
        <div
            style={{
                padding: '12px 16px',
                background: '#fff',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 13,
                lineHeight: 1.6,
                minHeight: 120,
            }}
        >
            {b ? (
                b
            ) : bPending ? (
                <TypingDots />
            ) : (
                <Text type="secondary">(empty)</Text>
            )}
        </div>
    </div>
);

interface Props {
    runId: number | null;
    subsystem: SubsystemId;
    jwt: string;
    onClose: () => void;
}

export const ComparisonView: React.FC<Props> = ({
    runId,
    subsystem,
    jwt,
    onClose,
}) => {
    const [detail, setDetail] = useState<SandboxRunDetail | null>(null);
    const [loading, setLoading] = useState(false);

    // Poll the run detail every 2s while status is queued/running. The runner
    // is fire-and-forget on the backend (Phase 4d) — POST /runs returns
    // immediately and progressively updates the row + per-side outputs as
    // each side completes. Polling stops as soon as status flips to complete
    // or error.
    useEffect(() => {
        if (runId == null) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        setLoading(true);

        const fetchOnce = async (): Promise<void> => {
            try {
                const d = await aiAdminApi.getSandboxRun(jwt, subsystem, runId);
                if (cancelled) return;
                setDetail(d);
                if (d.status === 'queued' || d.status === 'running') {
                    timeoutId = setTimeout(fetchOnce, 2000);
                }
            } catch (err) {
                // Stop polling on fetch errors so we don't hammer the API; the
                // user can close + reopen the drawer to retry.
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchOnce();

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [runId, subsystem, jwt]);

    const sideA = detail?.outputs.find((o) => o.side === 'a') ?? null;
    const sideB = detail?.outputs.find((o) => o.side === 'b') ?? null;
    const inputs = detail?.inputsJson as Record<string, unknown> | null;

    return (
        <Drawer
            open={runId != null}
            onClose={onClose}
            width={1100}
            title={
                detail ? (
                    <Space size="small">
                        <Text strong>Run #{detail.id}</Text>
                        <Tag color={STATUS_COLOUR[detail.status]}>
                            {detail.status}
                        </Tag>
                        <Text type="secondary">
                            v{detail.configAVersionNumber ?? '?'} vs v
                            {detail.configBVersionNumber ?? '?'}
                        </Text>
                    </Space>
                ) : (
                    'Run details'
                )
            }
        >
            {loading && !detail ? (
                <Skeleton active />
            ) : !detail ? (
                <Empty description="Run not found" />
            ) : detail.status === 'error' ? (
                <Alert
                    type="error"
                    showIcon
                    message="Run failed"
                    description={detail.errorMessage ?? 'Unknown error'}
                />
            ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {(detail.status === 'queued' ||
                        detail.status === 'running') && (
                        <Alert
                            type="info"
                            showIcon
                            message={
                                detail.status === 'queued'
                                    ? 'Run queued — about to start.'
                                    : sideA && !sideB
                                      ? 'Side A complete. Running Side B…'
                                      : sideB && !sideA
                                        ? 'Side B complete. Running Side A…'
                                        : 'Running both sides…'
                            }
                            description="Polling every 2s. Panels below fill in as each side completes."
                        />
                    )}
                    <Card size="small" title="Inputs">
                        <Descriptions
                            size="small"
                            column={2}
                            items={[
                                {
                                    key: 'scenario',
                                    label: 'Scenario',
                                    children:
                                        detail.scenarioName ?? (
                                            <Text type="secondary">Ad-hoc</Text>
                                        ),
                                },
                                {
                                    key: 'brand',
                                    label: 'Brand',
                                    children: detail.brandId ? (
                                        `#${detail.brandId}`
                                    ) : (
                                        <Text type="secondary">—</Text>
                                    ),
                                },
                                ...Object.entries(inputs ?? {}).map(
                                    ([k, v]) => ({
                                        key: k,
                                        label: k,
                                        children: (
                                            <Text>
                                                {typeof v === 'string'
                                                    ? v.length > 80
                                                        ? `${v.slice(0, 80)}…`
                                                        : v
                                                    : String(v)}
                                            </Text>
                                        ),
                                    }),
                                ),
                            ]}
                        />
                    </Card>

                    <Tabs
                        defaultActiveKey="output"
                        items={[
                            {
                                key: 'output',
                                label: 'Output',
                                children: (
                                    <SideBySideText
                                        a={getOutputText(sideA)}
                                        b={getOutputText(sideB)}
                                        aPending={
                                            !sideA &&
                                            (detail.status === 'queued' ||
                                                detail.status === 'running')
                                        }
                                        bPending={
                                            !sideB &&
                                            (detail.status === 'queued' ||
                                                detail.status === 'running')
                                        }
                                    />
                                ),
                            },
                            {
                                key: 'system',
                                label: 'System prompt',
                                children: (
                                    <SideBySideLineDiff
                                        before={sideA?.resolvedSystemPrompt ?? ''}
                                        after={sideB?.resolvedSystemPrompt ?? ''}
                                    />
                                ),
                            },
                            {
                                key: 'user',
                                label: 'User prompt',
                                children: (
                                    <SideBySideLineDiff
                                        before={sideA?.resolvedUserPrompt ?? ''}
                                        after={sideB?.resolvedUserPrompt ?? ''}
                                    />
                                ),
                            },
                            {
                                key: 'metadata',
                                label: 'Metadata',
                                children: <MetadataPanel a={sideA} b={sideB} />,
                            },
                            {
                                key: 'input-snapshot',
                                label: 'Input snapshot',
                                children: (
                                    <InputSnapshotPanel detail={detail} />
                                ),
                            },
                        ]}
                    />
                </Space>
            )}
        </Drawer>
    );
};

/**
 * Input-snapshot panel — shows everything the runner saw as input. Useful
 * for debugging "why did the runs differ?" questions and for documenting
 * a scenario's full input context. Renders the run's inputsJson plus the
 * resolved user prompt for either side (which contains brand-equity data
 * dumps + injected context).
 */
const InputSnapshotPanel: React.FC<{ detail: SandboxRunDetail }> = ({
    detail,
}) => {
    const sideA = detail.outputs.find((o) => o.side === 'a') ?? null;
    const sideB = detail.outputs.find((o) => o.side === 'b') ?? null;
    const samplePrompt = sideA?.resolvedUserPrompt ?? sideB?.resolvedUserPrompt;

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="inputsJson">
                <pre
                    style={{
                        margin: 0,
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        background: '#fafafa',
                        padding: 12,
                        borderRadius: 4,
                    }}
                >
                    {detail.inputsJson
                        ? JSON.stringify(detail.inputsJson, null, 2)
                        : '(none)'}
                </pre>
            </Card>
            {samplePrompt && (
                <Card
                    size="small"
                    title="Resolved user prompt (Side A)"
                    extra={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Includes brand-equity data + any injected context.
                            Both sides receive the same input — diff lives on
                            the System prompt + User prompt tabs.
                        </Text>
                    }
                >
                    <pre
                        style={{
                            margin: 0,
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            background: '#fafafa',
                            padding: 12,
                            borderRadius: 4,
                            maxHeight: 480,
                            overflow: 'auto',
                        }}
                    >
                        {samplePrompt}
                    </pre>
                </Card>
            )}
        </Space>
    );
};

interface MetadataPanelProps {
    a: SandboxRunOutput | null;
    b: SandboxRunOutput | null;
}

const MetadataPanel: React.FC<MetadataPanelProps> = ({ a, b }) => {
    const rows: Array<{
        label: string;
        getter: (s: SandboxRunOutput | null) => React.ReactNode;
    }> = [
        {
            label: 'Provider',
            getter: (s) => s?.provider ?? <Text type="secondary">—</Text>,
        },
        {
            label: 'Model',
            getter: (s) => s?.modelId ?? <Text type="secondary">—</Text>,
        },
        {
            label: 'Temperature',
            getter: (s) =>
                s?.temperature != null ? (
                    s.temperature.toString()
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            label: 'Input tokens',
            getter: (s) =>
                s?.inputTokens != null ? (
                    s.inputTokens.toLocaleString()
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            label: 'Output tokens',
            getter: (s) =>
                s?.outputTokens != null ? (
                    s.outputTokens.toLocaleString()
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            label: 'Latency',
            getter: (s) =>
                s?.latencyMs != null ? (
                    `${(s.latencyMs / 1000).toFixed(2)}s`
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            label: 'Cost',
            getter: (s) => {
                if (s == null) return <Text type="secondary">—</Text>;
                if (s.costUsd != null) return `$${s.costUsd.toFixed(6)}`;
                // Cost is null because the model registry has no pricing for
                // this model id (e.g. unreleased models seeded with `null`
                // for $/M). Surface that explicitly so the admin doesn't
                // think the run failed to track tokens.
                return (
                    <Tooltip
                        title={`No pricing data for ${s.modelId ?? '(unknown)'} in AVAILABLE_${(s.provider ?? '').toUpperCase()}_MODELS — edit on /admin/ai/models_providers to enable cost tracking.`}
                    >
                        <Text type="warning">Pricing unknown</Text>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <table
            style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
            }}
        >
            <thead>
                <tr>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: '#fafafa',
                            borderBottom: '1px solid #f0f0f0',
                            width: 160,
                        }}
                    />
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: '#fff1f0',
                            borderBottom: '1px solid #f0f0f0',
                        }}
                    >
                        Side A
                    </th>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            background: '#f6ffed',
                            borderBottom: '1px solid #f0f0f0',
                        }}
                    >
                        Side B
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.label}>
                        <th
                            style={{
                                textAlign: 'left',
                                padding: '6px 12px',
                                borderBottom: '1px solid #f5f5f5',
                                fontWeight: 500,
                            }}
                        >
                            {row.label}
                        </th>
                        <td
                            style={{
                                padding: '6px 12px',
                                borderBottom: '1px solid #f5f5f5',
                            }}
                        >
                            {row.getter(a)}
                        </td>
                        <td
                            style={{
                                padding: '6px 12px',
                                borderBottom: '1px solid #f5f5f5',
                            }}
                        >
                            {row.getter(b)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
